/*
 * <store-finder> — product-page store finder (US).
 * Customer enters a city/ZIP/address; we geocode via Google Maps and show the
 * nearest dealers that carry the product, ranked by distance. List only (no map).
 *
 * Config via data-attributes:
 *   data-dealers-url   URL of the dealers JSON asset
 *   data-product-key   product key to match against each dealer's `products`
 *   data-api-key       Google Maps JS API key (entered in theme editor)
 *   data-max-results   max stores to show (default 5)
 *   data-radius        optional max distance in miles (0/empty = no limit)
 */
(function () {
  if (customElements.get('store-finder')) return;

  var mapsPromise = null;
  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps && window.google.maps.Geocoder) {
      return Promise.resolve();
    }
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey);
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        mapsPromise = null;
        reject(new Error('maps-load-failed'));
      };
      document.head.appendChild(s);
    });
    return mapsPromise;
  }

  function haversineMiles(a, b) {
    var R = 3958.8; // Earth radius in miles
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(b.lat - a.lat);
    var dLng = toRad(b.lng - a.lng);
    var lat1 = toRad(a.lat);
    var lat2 = toRad(b.lat);
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  class StoreFinder extends HTMLElement {
    connectedCallback() {
      this.apiKey = this.dataset.apiKey || '';
      this.productKey = this.dataset.productKey || '';
      this.maxResults = parseInt(this.dataset.maxResults, 10) || 5;
      this.radius = parseFloat(this.dataset.radius) || 0;
      this.dealersUrl = this.dataset.dealersUrl || '';
      this.dealers = null;

      this.form = this.querySelector('.store-finder__form');
      this.input = this.querySelector('.store-finder__input');
      this.button = this.querySelector('.store-finder__button');
      this.status = this.querySelector('.store-finder__status');
      this.results = this.querySelector('.store-finder__results');

      if (!this.form) return;

      // Directions links use target="_blank", but some sandboxed iframe contexts
      // (e.g. the Shopify theme editor preview) downgrade that to in-frame
      // navigation, which Google blocks ("refused to connect"). Opening via an
      // explicit user-gesture popup forces a real top-level tab; if the browser
      // blocks the popup we fall back to the link's default behaviour.
      if (this.results) {
        this.results.addEventListener('click', function (e) {
          var a = e.target.closest && e.target.closest('.store-finder__directions');
          if (!a) return;
          var w = window.open(a.href, '_blank', 'noopener');
          if (w) e.preventDefault();
        });
      }

      if (!this.apiKey) {
        this.setStatus('Store finder is not configured yet.', 'info');
        if (this.button) this.button.disabled = true;
        return;
      }

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setStatus(msg, state) {
      if (!this.status) return;
      this.status.textContent = msg || '';
      if (state) this.status.setAttribute('data-state', state);
      else this.status.removeAttribute('data-state');
    }

    async getDealers() {
      if (this.dealers) return this.dealers;
      var res = await fetch(this.dealersUrl, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('dealers-fetch-failed');
      var data = await res.json();
      this.dealers = (data && data.dealers) || [];
      return this.dealers;
    }

    async onSubmit(e) {
      e.preventDefault();
      var query = (this.input && this.input.value || '').trim();
      if (!query) {
        this.setStatus('Enter a city or ZIP code to search.', 'error');
        return;
      }
      this.setStatus('Searching…', 'info');
      if (this.results) this.results.innerHTML = '';
      if (this.button) this.button.disabled = true;

      try {
        var dealers = await this.getDealers();
        await loadGoogleMaps(this.apiKey);
        var origin = await this.geocode(query);
        this.renderResults(origin, dealers);
      } catch (err) {
        if (err && err.message === 'maps-load-failed') {
          this.setStatus('Map service is unavailable right now. Please try again later.', 'error');
        } else if (err && err.message === 'no-geocode') {
          this.setStatus('We could not find that location. Try a city or ZIP code.', 'error');
        } else if (err && err.message === 'geocode-config') {
          console.error('[store-finder] Geocoder unavailable. Status:', err.status,
            '— ensure the Maps JavaScript API and Geocoding API are enabled (with billing) for this key.');
          this.setStatus('Store search is temporarily unavailable. Please try again later.', 'error');
        } else {
          this.setStatus('Something went wrong. Please try again.', 'error');
        }
      } finally {
        if (this.button) this.button.disabled = false;
      }
    }

    geocode(query) {
      var geocoder = new window.google.maps.Geocoder();
      return new Promise(function (resolve, reject) {
        geocoder.geocode(
          { address: query, componentRestrictions: { country: 'US' } },
          function (results, statusCode) {
            if (statusCode === 'OK' && results && results[0]) {
              var loc = results[0].geometry.location;
              resolve({ lat: loc.lat(), lng: loc.lng() });
            } else if (statusCode === 'ZERO_RESULTS') {
              reject(new Error('no-geocode'));
            } else {
              // REQUEST_DENIED / OVER_QUERY_LIMIT / etc. — a configuration problem,
              // not a bad customer input. Surface a generic message; log details.
              var e = new Error('geocode-config');
              e.status = statusCode;
              reject(e);
            }
          }
        );
      });
    }

    renderResults(origin, dealers) {
      var key = this.productKey;
      var matched = dealers
        .filter(function (d) {
          return d && typeof d.lat === 'number' && typeof d.lng === 'number' &&
            (!key || (Array.isArray(d.products) && d.products.indexOf(key) !== -1));
        })
        .map(function (d) {
          return { dealer: d, distance: haversineMiles(origin, { lat: d.lat, lng: d.lng }) };
        })
        .sort(function (a, b) { return a.distance - b.distance; });

      if (this.radius > 0) {
        matched = matched.filter(function (m) { return m.distance <= this.radius; }, this);
      }
      matched = matched.slice(0, this.maxResults);

      if (!matched.length) {
        this.setStatus(
          this.radius > 0
            ? 'No stores found within ' + this.radius + ' miles.'
            : 'No nearby stores carry this product yet. Please contact us.',
          'info'
        );
        return;
      }

      this.setStatus(matched.length + (matched.length === 1 ? ' store' : ' stores') + ' near you:', null);
      this.results.innerHTML = matched.map(function (m) {
        var d = m.dealer;
        var miles = m.distance < 10 ? m.distance.toFixed(1) : Math.round(m.distance);
        var dir = 'https://www.google.com/maps/dir/?api=1&destination=' + d.lat + ',' + d.lng;
        var addr = [d.address, [d.city, d.state].filter(Boolean).join(', '), d.zip]
          .filter(Boolean).join(', ');
        return (
          '<li class="store-finder__item">' +
            '<div>' +
              '<div class="store-finder__name">' + esc(d.name) + '</div>' +
              '<div class="store-finder__addr">' + esc(addr) + '</div>' +
            '</div>' +
            '<div class="store-finder__meta">' +
              '<span class="store-finder__distance">' + miles + ' mi</span>' +
              '<a class="store-finder__directions" href="' + dir + '" target="_blank" rel="noopener">Directions</a>' +
            '</div>' +
          '</li>'
        );
      }).join('');
    }
  }

  customElements.define('store-finder', StoreFinder);
})();
